import React, { useState, useEffect } from 'react';
import { Heart, X, RotateCcw, Sparkles, Users } from 'lucide-react';
import SwipeCard from './SwipeCard';
import axios from 'axios';
import { toast } from 'sonner';

const SwipeProfiles = ({ user }) => {
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swipeHistory, setSwipeHistory] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get('/matches/swipe-profiles');
      setProfiles(response.data);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = async (profileId, direction) => {
    if (isAnimating) return;
    setIsAnimating(true);

    const currentProfile = profiles[currentIndex];
    if (!currentProfile) return;

    // Add to history for undo functionality
    setSwipeHistory(prev => [...prev, { profile: currentProfile, direction, index: currentIndex }]);

    try {
      // Send swipe action to backend
      await axios.post('/matches/swipe', {
        target_user_id: profileId,
        action: direction === 'right' ? 'like' : 'pass'
      });

      if (direction === 'right') {
        toast.success('Profile liked! 💚', { duration: 1500 });
        
        // Check if this creates a match
        try {
          const matchResponse = await axios.get(`/matches/check-match/${profileId}`);
          if (matchResponse.data.is_match) {
            toast.success('🎉 It\'s a Match! You can now message each other!', { duration: 4000 });
          }
        } catch (matchError) {
          console.error('Error checking match:', matchError);
        }
      }

      // Move to next profile
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setIsAnimating(false);
      }, 300);

    } catch (error) {
      console.error('Error processing swipe:', error);
      toast.error('Failed to process swipe');
      setIsAnimating(false);
    }
  };

  const handleButtonSwipe = (direction) => {
    const currentProfile = profiles[currentIndex];
    if (currentProfile && !isAnimating) {
      handleSwipe(currentProfile.id, direction);
    }
  };

  const handleUndo = async () => {
    if (swipeHistory.length === 0 || isAnimating) return;

    const lastSwipe = swipeHistory[swipeHistory.length - 1];
    
    try {
      // Send undo request to backend
      await axios.post('/matches/undo-swipe', {
        target_user_id: lastSwipe.profile.id
      });

      // Remove from history and go back
      setSwipeHistory(prev => prev.slice(0, -1));
      setCurrentIndex(lastSwipe.index);
      toast.success('Swipe undone!', { duration: 1500 });

    } catch (error) {
      console.error('Error undoing swipe:', error);
      toast.error('Failed to undo swipe');
    }
  };

  const getCurrentProfile = () => profiles[currentIndex];
  const getNextProfile = () => profiles[currentIndex + 1];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-emerald-700 dark:text-emerald-300 font-medium">Loading profiles...</p>
        </div>
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center card max-w-md">
          <Users className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-2">
            No More Profiles
          </h3>
          <p className="text-emerald-600 dark:text-emerald-300 mb-4">
            You've seen all available profiles! Reset to browse again.
          </p>
          <div className="flex flex-col space-y-3">
            <button
              onClick={fetchProfiles}
              className="btn-primary flex items-center space-x-2 mx-auto"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
            <button
              onClick={async () => {
                try {
                  setLoading(true);
                  await axios.post('/matches/reset-swipes');
                  await fetchProfiles();
                  toast.success('Swipes reset! You can browse profiles again.');
                } catch (error) {
                  console.error('Error resetting swipes:', error);
                  toast.error('Failed to reset swipes');
                  setLoading(false);
                }
              }}
              className="btn-secondary flex items-center space-x-2 mx-auto"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset & Browse Again</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentIndex >= profiles.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center card max-w-md">
          <Sparkles className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-2">
            You're All Caught Up!
          </h3>
          <p className="text-emerald-600 dark:text-emerald-300 mb-4">
            Great job exploring! You can refresh to see profiles again.
          </p>
          <div className="flex flex-col space-y-3">
            <button
              onClick={fetchProfiles}
              className="btn-primary flex items-center space-x-2 mx-auto"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Check for New Profiles</span>
            </button>
            <button
              onClick={async () => {
                try {
                  setLoading(true);
                  await axios.post('/matches/reset-swipes');
                  await fetchProfiles();
                  toast.success('Swipes reset! You can browse profiles again.');
                } catch (error) {
                  console.error('Error resetting swipes:', error);
                  toast.error('Failed to reset swipes');
                  setLoading(false);
                }
              }}
              className="btn-secondary flex items-center space-x-2 mx-auto"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset & Browse Again</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      {/* Swipe Area */}
      <div className="relative h-[600px] mb-6">
        {/* Next card (behind) */}
        {getNextProfile() && (
          <SwipeCard
            key={`${getNextProfile().id}-next`}
            profile={getNextProfile()}
            onSwipe={handleSwipe}
            isTop={false}
          />
        )}
        
        {/* Current card (top) */}
        {getCurrentProfile() && (
          <SwipeCard
            key={`${getCurrentProfile().id}-current`}
            profile={getCurrentProfile()}
            onSwipe={handleSwipe}
            isTop={true}
          />
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center items-center space-x-6 mb-4">
        {/* Pass Button */}
        <button
          onClick={() => handleButtonSwipe('left')}
          disabled={isAnimating}
          className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:opacity-50 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
          data-testid="pass-button"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* Undo Button */}
        {swipeHistory.length > 0 && (
          <button
            onClick={handleUndo}
            disabled={isAnimating}
            className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 disabled:opacity-50 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
            data-testid="undo-button"
          >
            <RotateCcw className="w-5 h-5 text-white" />
          </button>
        )}

        {/* Like Button */}
        <button
          onClick={() => handleButtonSwipe('right')}
          disabled={isAnimating}
          className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
          data-testid="like-button"
        >
          <Heart className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Instructions */}
      <div className="text-center">
        <p className="text-sm text-emerald-600 dark:text-emerald-300 mb-2">
          Swipe right to like • Swipe left to pass
        </p>
        <p className="text-xs text-emerald-500 dark:text-emerald-400">
          {profiles.length - currentIndex} profiles remaining
        </p>
      </div>
    </div>
  );
};

export default SwipeProfiles;